import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../base/Page';
import { Styles } from '../../base/Styles';
import { useOrders, useOrderItems, useUpdateOrderLoadedStatus } from '../../base/useDbOrders';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import { Order } from '../../model/Order';
import { useState } from 'react';

export default function Orders() {
  const { orders } = useOrders();
  const navigate = useNavigate();

  return (
    <Page title="Orders">
      <div className="flex justify-end mb-4">
        <button
          type="button"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
          onClick={() => navigate('/products/add')}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Add New Order
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {!orders || orders.length === 0 ? (
          <div className="col-span-full text-center py-10 bg-gray-100 rounded-lg">
            <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6" />
            </svg>
            <p className="mt-2 text-gray-600">No orders found</p>
          </div>
        ) : (
          orders.map((order) => (
            <OrderCard key={order.orderId} order={order} />
          ))
        )}
      </div>
    </Page>
  );
}

function OrderCard({ order }: { order: Order }) {
  const navigate = useNavigate();
  const { orderProducts, loading } = useOrderItems(order.orderId || '');
  const { updateOrderLoadedStatus } = useUpdateOrderLoadedStatus(order.orderId || '');

  const date = order.orderDate ? format(new Date(Number(order.orderDate)), 'PPp') : '';

  // Local state for loadedForDelivery
  const [loadedForDelivery, setLoadedForDelivery] = useState(order.loadedForDelivery || false);

  // Calculate ready, no stock, and total items
  const readyCount = orderProducts?.filter(p => p.readyForDelivery && !p.noStock).length || 0;
  const noStockCount = orderProducts?.filter(p => p.noStock).length || 0;
  const totalCount = (orderProducts?.length || 0) - noStockCount; // Exclude noStock items from total
  const allReady = totalCount > 0 && readyCount === totalCount;

  // Determine card background color
  const hasNoStock = noStockCount > 0;
  const cardClass = loadedForDelivery
    ? 'p-4 bg-yellow-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200'
    : hasNoStock && !allReady
      ? 'p-4 bg-red-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200'
      : allReady
        ? 'p-4 bg-green-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200'
        : 'p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200';

  if (loading) return <div className="p-4 bg-white rounded-lg">Loading...</div>;

  // Clean phone number and create WhatsApp links
  const phoneNumberRaw = order.deliveryPhone || '';
  const phoneNumberClean = phoneNumberRaw.replace(/[^0-9+]/g, '');
  const phoneNumber = phoneNumberClean.startsWith('+')
    ? phoneNumberClean
    : `+81${phoneNumberClean.replace(/^0/, '')}`;

  const whatsappCallLink = `https://wa.me/${phoneNumber}`;
  const receivedMessage = encodeURIComponent(`Hello ${order.deliveryName}, your order #${order.orderId} has been received. We'll notify you when it's ready.`);
  const readyMessage = encodeURIComponent(`Hello ${order.deliveryName}, your order #${order.orderId} is ready for delivery/pickup. Thank you!`);
  const loadedMessage = encodeURIComponent(`Hello ${order.deliveryName}, your order #${order.orderId} has been loaded for delivery. We'll update you on its progress.`);
  const whatsappReceivedLink = `https://wa.me/${phoneNumber}?text=${receivedMessage}`;
  const whatsappReadyLink = `https://wa.me/${phoneNumber}?text=${readyMessage}`;
  const whatsappLoadedLink = `https://wa.me/${phoneNumber}?text=${loadedMessage}`;

  // Handle checkbox change
  const handleLoadedChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setLoadedForDelivery(newValue);
    try {
      await updateOrderLoadedStatus(newValue);
    } catch (error) {
      // Revert state on failure
      setLoadedForDelivery(!newValue);
    }
  };

  return (
    <div className={cardClass}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm text-gray-500">#{order.orderId}</span>
        <span className="text-sm font-semibold text-gray-700">{date}</span>
      </div>
      <div className="flex flex-col gap-1 mb-3">
        <span className="font-semibold text-lg">{order.deliveryName}</span>
        <span className="text-gray-600">{order.deliveryAddress}</span>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-gray-600">{order.deliveryPhone}</span>
          {phoneNumber && (
            <div className="flex gap-2 flex-wrap">
              <a
                href={whatsappCallLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-200 text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.496.074-.149-.15-.672-.648-.822-.797-.15-.15-.22-.348-.148-.496.074-.149.372-.72.648-.995.273-.297.595-.642.942-.942.297-.298.494-.497.74-.347.246.149 1.469.716 1.766.965.297.298.397.647.298.996-.099.348-.596 1.045-.694 1.194zm-5.49-12.377c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6.405 17.822c-.543 1.563-1.672 2.691-3.235 3.234-1.563.543-3.234.297-4.648-.645-1.413-.943-2.444-2.363-2.987-3.926-.543-1.563-.297-3.234.645-4.647.943-1.413 2.363-2.444 3.926-2.987 1.563-.543 3.234-.297 4.647.645 1.413.943 2.444 2.363 2.987 3.926.543 1.563.297 3.234-.645 4.647z" />
                </svg>
                Call
              </a>
              <a
                href={whatsappReceivedLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-200 text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.496.074-.149-.15-.672-.648-.822-.797-.15-.15-.22-.348-.148-.496.074-.149.372-.72.648-.995.273-.297.595-.642.942-.942.297-.298.494-.497.74-.347.246.149 1.469.716 1.766.965.297.298.397.647.298.996-.099.348-.596 1.045-.694 1.194zm-5.49-12.377c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6.405 17.822c-.543 1.563-1.672 2.691-3.235 3.234-1.563.543-3.234.297-4.648-.645-1.413-.943-2.444-2.363-2.987-3.926-.543-1.563-.297-3.234.645-4.647.943-1.413 2.363-2.444 3.926-2.987 1.563-.543 3.234-.297 4.647.645 1.413.943 2.444 2.363 2.987 3.926.543 1.563.297 3.234-.645 4.647z" />
                </svg>
                Order Received
              </a>
              <a
                href={whatsappReadyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-200 text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.496.074-.149-.15-.672-.648-.822-.797-.15-.15-.22-.348-.148-.496.074-.149.372-.72.648-.995.273-.297.595-.642.942-.942.297-.298.494-.497.74-.347.246.149 1.469.716 1.766.965.297.298.397.647.298.996-.099.348-.596 1.045-.694 1.194zm-5.49-12.377c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6.405 17.822c-.543 1.563-1.672 2.691-3.235 3.234-1.563.543-3.234.297-4.648-.645-1.413-.943-2.444-2.363-2.987-3.926-.543-1.563-.297-3.234.645-4.647.943-1.413 2.363-2.444 3.926-2.987 1.563-.543 3.234-.297 4.647.645 1.413.943 2.444 2.363 2.987 3.926.543 1.563.297 3.234-.645 4.647z" />
                </svg>
                Order Ready
              </a>
              <a
                href={whatsappLoadedLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-200 text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.496.074-.149-.15-.672-.648-.822-.797-.15-.15-.22-.348-.148-.496.074-.149.372-.72.648-.995.273-.297.595-.642.942-.942.297-.298.494-.497.74-.347.246.149 1.469.716 1.766.965.297.298.397.647.298.996-.099.348-.596 1.045-.694 1.194zm-5.49-12.377c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6.405 17.822c-.543 1.563-1.672 2.691-3.235 3.234-1.563.543-3.234.297-4.648-.645-1.413-.943-2.444-2.363-2.987-3.926-.543-1.563-.297-3.234.645-4.647.943-1.413 2.363-2.444 3.926-2.987 1.563-.543 3.234-.297 4.647.645 1.413.943 2.444 2.363 2.987 3.926.543 1.563.297 3.234-.645 4.647z" />
                </svg>
                Order Loaded
              </a>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center border-t pt-2">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">{order.orderStatus}</span>
          <span className="text-sm text-gray-600 font-bold text-lg">
            Ready: {readyCount}/{totalCount}
          </span>
          {noStockCount > 0 && (
            <span className="text-sm text-red-600">
              No Stock: {noStockCount} item{noStockCount > 1 ? 's' : ''}
            </span>
          )}
          <label className="flex items-center text-sm text-gray-600 mt-1">
            <input
              type="checkbox"
              checked={loadedForDelivery}
              onChange={handleLoadedChange}
              className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            Loaded for Delivery
          </label>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">¥</span>
          <span className="text-lg font-bold">
            {new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(order.totalAmount)}
          </span>
        </div>
        <button
          className="text-blue-600 underline hover:text-blue-800 transition-colors"
          onClick={() => navigate(`/orders/${order.orderId}`)}
        >
          Edit
        </button>
      </div>
    </div>
  );
}